import { motion } from "framer-motion";
import { ArrowRight, Home, Shield, TrendingUp, Building2, TreePine } from "lucide-react";
import { Link } from "react-router-dom";

const benefits = [
  { icon: TrendingUp, title: "Rámcový odhad", desc: "Na základě aktuálních dat trhu" },
  { icon: Shield, title: "Bez závazků", desc: "Zcela zdarma a nezávazně" },
  { icon: Home, title: "Do 48 hodin", desc: "Rychlá odpověď s analýzou" },
];

const types = [
  { icon: Building2, label: "Tržní odhad – Byt", href: "/sluzby/odhad/byt", gradient: "from-blue-600 to-primary", glow: "hsl(var(--primary) / 0.25)" },
  { icon: Home, label: "Tržní odhad – Dům", href: "/sluzby/odhad/dum", gradient: "from-emerald-600 to-emerald-500", glow: "rgba(16,185,129,0.25)" },
  { icon: TreePine, label: "Tržní odhad – Pozemek", href: "/sluzby/odhad/pozemek", gradient: "from-amber-600 to-amber-500", glow: "rgba(245,158,11,0.25)" },
];

const ease = [0.22, 1, 0.36, 1] as const;

const LeadMagnetSection = () => {
  return (
    <section id="odhad" className="section-padding bg-gradient-to-b from-background to-muted/30">
      <div className="container-luxury">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: benefits */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease }}
          >
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1, ease }}
              className="badge-remax mb-6"
            >
              Bezplatný odhad
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2, ease }}
              className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6 leading-tight"
            >
              Odhad tržní ceny{" "}
              <span className="text-primary">zdarma</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3, ease }}
              className="text-muted-foreground text-lg mb-10 max-w-md leading-relaxed"
            >
              Zjistěte reálnou hodnotu vaší nemovitosti od experta s 30letou praxí. Žádné háčky, žádné závazky.
            </motion.p>

            <div className="space-y-4">
              {benefits.map((b, i) => (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.35 + i * 0.12, ease }}
                  whileHover={{ x: 8, scale: 1.02, transition: { duration: 0.25 } }}
                  className="btn-glass-hover group flex items-center gap-4 bg-card backdrop-blur-sm rounded-2xl border-2 border-border/30 px-5 py-5 shadow-[0_2px_12px_hsl(var(--foreground)/0.04)] hover:border-primary/35 hover:shadow-[0_8px_35px_-8px_hsl(var(--primary)/0.22),0_2px_8px_hsl(var(--primary)/0.06)] hover:bg-card transition-all duration-400 cursor-default"
                >
                  <motion.div
                    whileHover={{ scale: 1.15, rotate: 8 }}
                    transition={{ type: "spring", stiffness: 400, damping: 12 }}
                    className="icon-glossy relative flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/[0.12] to-primary/[0.04] border border-primary/10 flex items-center justify-center group-hover:from-primary/[0.25] group-hover:to-primary/[0.12] group-hover:border-primary/30 transition-all duration-300"
                  >
                    <b.icon className="h-5 w-5 text-primary transition-all duration-300 group-hover:scale-125" />
                  </motion.div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground group-hover:text-primary transition-colors duration-300">{b.title}</h3>
                    <p className="text-sm text-muted-foreground">{b.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: type selection cards */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.15, ease }}
          >
            <div className="glass-card p-8 sm:p-10">
              <motion.h3
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3, ease }}
                className="font-heading font-bold text-xl text-foreground mb-2"
              >
                Vyberte typ nemovitosti
              </motion.h3>
              <p className="text-sm text-muted-foreground mb-8">
                Zvolte kategorii a vyplňte krátký dotazník – odhad obdržíte do 48 hodin.
              </p>

              <div className="grid gap-4">
                {types.map((t, i) => (
                  <motion.div
                    key={t.label}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.4 + i * 0.1, ease }}
                  >
                    <Link
                      to={t.href}
                      className="group flex items-center gap-5 bg-secondary/50 rounded-2xl border border-border/50 px-5 py-5 hover:border-primary/30 hover:shadow-[0_8px_30px_-8px_hsl(var(--primary)/0.18)] hover:bg-secondary/80 transition-all duration-300"
                    >
                      <motion.div
                        whileHover={{ scale: 1.15, rotate: 8 }}
                        transition={{ type: "spring", stiffness: 400, damping: 12 }}
                        className={`icon-glossy relative w-14 h-14 rounded-2xl bg-gradient-to-br ${t.gradient} flex items-center justify-center shrink-0`}
                        style={{ boxShadow: `0 8px 24px -4px ${t.glow}, inset 0 1px 1px rgba(255,255,255,0.2)` }}
                      >
                        <t.icon className="h-6 w-6 text-white" />
                      </motion.div>
                      <div className="flex-1">
                        <h4 className="font-heading font-semibold text-foreground group-hover:text-primary transition-colors duration-300">{t.label}</h4>
                        <p className="text-xs text-muted-foreground mt-0.5">Vyplnit dotazník →</p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300" />
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LeadMagnetSection;
